import React, { useState, useRef, useCallback } from 'react';
import { Button } from '~/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '~/components/ui/card';
import { Badge } from '~/components/ui/badge';
import { Switch } from '~/components/ui/switch';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '~/components/ui/alert-dialog';
import { Upload, FileText, X, Download, Play, Loader2, AlertCircle, CheckCircle2, Clock, AlertTriangle, Eye } from 'lucide-react';
import { Project, FileRecord } from '../types/api';
import { useProjectFiles, useUploadFile, useProcessFile, useDeleteFile } from '../hooks/useFiles';
import { useAuth } from '../contexts/AuthContext';
import { processingService } from '../services/processing';
import { useWebSocketProcessing, ProcessingUpdateData } from '../hooks/useWebSocketProcessing';

interface EnhancedProjectFilesProps {
  project: Project;
  projectId: string;
}

export function EnhancedProjectFiles({ project, projectId }: EnhancedProjectFilesProps) {
  const [isDragOver, setIsDragOver] = useState(false);
  const [autoProcess, setAutoProcess] = useState(false);
  const [processingResults, setProcessingResults] = useState<Record<string, any>>({});
  const [processingStatus, setProcessingStatus] = useState<Record<string, { status: string; currentStep?: string; errorMessage?: string }>>({});
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { user } = useAuth();

  const { data: files = [], isLoading, refetch } = useProjectFiles(projectId);
  const uploadFileMutation = useUploadFile();
  const processFileMutation = useProcessFile();
  const deleteFileMutation = useDeleteFile();

  // WebSocket processing updates
  const handleProcessingUpdate = useCallback((data: ProcessingUpdateData) => {
    console.log('[EnhancedProjectFiles] Processing update received:', data);
    setProcessingStatus(prev => ({
      ...prev,
      [data.fileId]: {
        status: data.status,
        errorMessage: data.errorMessage
      }
    }));
    // Refresh file list to get updated status
    refetch();
  }, [refetch]);

  useWebSocketProcessing(handleProcessingUpdate);

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const getStatusIcon = (file: FileRecord) => {
    const localStatus = processingStatus[file.id]?.status;
    const status = localStatus || file.processingStatus;

    switch (status) {
      case 'unprocessed':
        return <Clock className="h-4 w-4 text-yellow-500" />;
      case 'processing':
        return <Loader2 className="h-4 w-4 text-blue-500 animate-spin" />;
      case 'completed':
        return <CheckCircle2 className="h-4 w-4 text-green-500" />;
      case 'failed':
        return <AlertTriangle className="h-4 w-4 text-red-500" />;
      default:
        return <Clock className="h-4 w-4 text-gray-500" />;
    }
  };

  const getStatusBadge = (file: FileRecord) => {
    const localStatus = processingStatus[file.id]?.status;
    const status = (localStatus || file.processingStatus) as FileRecord['processingStatus'];

    const variants = {
      unprocessed: 'secondary',
      processing: 'default',
      completed: 'default',
      failed: 'destructive'
    } as const;

    const colors = {
      unprocessed: 'bg-yellow-100 text-yellow-800',
      processing: 'bg-blue-100 text-blue-800',
      completed: 'bg-green-100 text-green-800',
      failed: 'bg-red-100 text-red-800'
    };

    return (
      <Badge
        variant={variants[status]}
        className={`text-xs ${colors[status]}`}
      >
        {status === 'unprocessed' && 'Unprocessed'}
        {status === 'processing' && 'Processing'}
        {status === 'completed' && 'Completed'}
        {status === 'failed' && 'Failed'}
      </Badge>
    );
  };

  const handleFileUpload = async (selectedFiles: FileList | null) => {
    if (!selectedFiles) return;

    const fileArray = Array.from(selectedFiles);
    
    for (const file of fileArray) {
      // Validate file type (PDF only)
      if (file.type !== 'application/pdf') {
        console.error(`File ${file.name} is not a PDF file`);
        continue;
      }

      try {
        const uploadData = {
          filename: file.name,
          mimeType: file.type,
          fileSize: file.size,
        };

        console.log('Uploading file:', uploadData, 'Auto-process:', autoProcess);

        const response = await uploadFileMutation.mutateAsync({
          projectId,
          file: uploadData,
          autoProcess,
        });

        if (response.success && response.data) {
          // Upload the file to S3
          const { uploadUrl } = response.data;
          
          const uploadResponse = await fetch(uploadUrl, {
            method: 'PUT',
            body: file,
            headers: {
              'Content-Type': file.type,
            },
          });

          if (uploadResponse.ok) {
            console.log(`File ${file.name} uploaded successfully`);
            // Refresh the file list
            refetch();
          } else {
            console.error(`Failed to upload ${file.name} to S3`);
          }
        }
      } catch (error: any) {
        console.error(`Error uploading ${file.name}:`, error);
      }
    }
  };

  const handleProcessFile = async (fileId: string) => {
    try {
      console.log(`[EnhancedProjectFiles] Starting processing for file ${fileId}`);

      // Update local status immediately
      setProcessingStatus(prev => ({
        ...prev,
        [fileId]: { status: 'processing' }
      }));

      // Start processing with the new service
      await processingService.startProcessing(fileId, {
        onStatusUpdate: (status: string, currentStep?: string) => {
          console.log(`[EnhancedProjectFiles] Polling status update for ${fileId}: ${status}`);
          setProcessingStatus(prev => ({
            ...prev,
            [fileId]: { status, currentStep }
          }));
        },
        onError: (errorMessage: string) => {
          console.error(`[EnhancedProjectFiles] Processing error for ${fileId}:`, errorMessage);
          setProcessingStatus(prev => ({
            ...prev,
            [fileId]: { status: 'failed' }
          }));
        },
        onComplete: (results: any) => {
          console.log(`[EnhancedProjectFiles] Processing results received for ${fileId}`);
          setProcessingResults(prev => ({
            ...prev,
            [fileId]: results
          }));
        }
      });

      // The processing service will handle polling and WebSocket updates
      console.log(`[EnhancedProjectFiles] Processing started for file ${fileId}`);
    } catch (error: any) {
      console.error(`[EnhancedProjectFiles] Error processing file ${fileId}:`, error);
      setProcessingStatus(prev => ({
        ...prev,
        [fileId]: { status: 'failed' }
      }));
    }
  };

  const handleDeleteFile = async (fileId: string) => {
    try {
      await deleteFileMutation.mutateAsync(fileId);
      refetch(); // Refresh the file list
    } catch (error: any) {
      console.error('Error deleting file:', error);
    }
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
    handleFileUpload(e.dataTransfer.files);
  };

  const canProcess = (file: FileRecord) => {
    return file.processingStatus === 'unprocessed' && 
           user && 
           user.creditsBalance >= (file.pageCount || 1);
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-32">
        <Loader2 className="h-8 w-8 animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Project Files</CardTitle>
          <CardDescription>
            Upload PDF files for bank statement parsing. Only PDF files are supported.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {/* Auto-processing toggle */}
          <div className="flex items-center space-x-2 mb-6 p-4 bg-gray-50 rounded-lg">
            <Switch
              id="auto-process"
              checked={autoProcess}
              onCheckedChange={setAutoProcess}
            />
            <div className="space-y-1">
              <label htmlFor="auto-process" className="text-sm font-medium cursor-pointer">
                Auto-process files
              </label>
              <p className="text-xs text-muted-foreground">
                Automatically start processing files after upload (uses credits)
              </p>
            </div>
          </div>

          {/* Upload area */}
          <div
            className={`border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
              isDragOver
                ? 'border-primary bg-primary/5'
                : 'border-muted-foreground/25 hover:border-muted-foreground/50'
            }`}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
          >
            <Upload className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
            <h3 className="mb-2">Upload PDF Files</h3>
            <p className="text-sm text-muted-foreground mb-4">
              Drag and drop PDF files here, or click to browse
            </p>
            <Button
              onClick={() => fileInputRef.current?.click()}
              variant="outline"
              disabled={uploadFileMutation.isPending}
            >
              {uploadFileMutation.isPending ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Uploading...
                </>
              ) : (
                'Browse Files'
              )}
            </Button>
            <input
              ref={fileInputRef}
              type="file"
              multiple
              className="hidden"
              onChange={(e) => handleFileUpload(e.target.files)}
              accept=".pdf,application/pdf"
            />
          </div>

          {/* Files list */}
          {files.length > 0 && (
            <div className="mt-6">
              <h4 className="mb-4">Uploaded Files ({files.length})</h4>
              <div className="space-y-2">
                {files.map((file) => (
                  <div
                    key={file.id}
                    className="flex items-center justify-between p-3 border rounded-lg bg-card"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <FileText className="h-5 w-5 text-red-500 flex-shrink-0" />
                      <div className="flex-1 min-w-0">
                        <p className="truncate font-medium">{file.filename}</p>
                        <div className="flex items-center gap-2 mt-1">
                          <Badge variant="secondary" className="text-xs">
                            {formatFileSize(file.fileSize)}
                          </Badge>
                          {file.pageCount && (
                            <Badge variant="outline" className="text-xs">
                              {file.pageCount} page{file.pageCount !== 1 ? 's' : ''}
                            </Badge>
                          )}
                          {getStatusBadge(file)}
                        </div>
                        {(file.errorMessage || processingStatus[file.id]?.errorMessage) && (
                          <p className="text-xs text-red-600 mt-1 flex items-center gap-1">
                            <AlertCircle className="h-3 w-3" />
                            {processingStatus[file.id]?.errorMessage || file.errorMessage}
                          </p>
                        )}
                      </div>
                      <div className="flex items-center gap-1">
                        {getStatusIcon(file)}
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {/* Process button */}
                      {canProcess(file) && (
                        <Button
                          onClick={() => handleProcessFile(file.id)}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3"
                          disabled={processFileMutation.isPending}
                        >
                          {processFileMutation.isPending && processFileMutation.variables === file.id ? (
                            <Loader2 className="h-3 w-3 animate-spin" />
                          ) : (
                            <>
                              <Play className="h-3 w-3 mr-1" />
                              Process
                            </>
                          )}
                        </Button>
                      )}

                      {/* View Results button */}
                      {(file.processingStatus === 'completed' || processingStatus[file.id]?.status === 'completed') && (
                        <Button
                          onClick={() => {
                            const results = processingResults[file.id];
                            if (results) {
                              // Open results in new tab or modal
                              console.log('Viewing results:', results);
                              // TODO: Implement results viewer
                            } else {
                              console.log('No results available for file:', file.id);
                            }
                          }}
                          variant="outline"
                          size="sm"
                          className="h-8 px-3"
                          title="View processing results"
                        >
                          <Eye className="h-3 w-3 mr-1" />
                          View Results
                        </Button>
                      )}

                      {/* Download button */}
                      <Button
                        variant="ghost"
                        size="sm"
                        className="h-8 w-8 p-0"
                        title="Download file"
                      >
                        <Download className="h-4 w-4" />
                      </Button>

                      {/* Delete button */}
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 text-destructive hover:text-destructive"
                            disabled={deleteFileMutation.isPending}
                          >
                            {deleteFileMutation.isPending && deleteFileMutation.variables === file.id ? (
                              <Loader2 className="h-3 w-3 animate-spin" />
                            ) : (
                              <X className="h-4 w-4" />
                            )}
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete File</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to delete "{file.filename}"? This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => handleDeleteFile(file.id)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </div>
                ))}
              </div>
              
              {/* Summary */}
              <div className="mt-4 flex items-center justify-between text-sm text-muted-foreground">
                <span>
                  Total: {formatFileSize(files.reduce((acc, file) => acc + file.fileSize, 0))}
                </span>
                <span>
                  Pages: {files.reduce((acc, file) => acc + (file.pageCount || 0), 0)}
                </span>
              </div>
            </div>
          )}

          {/* Credits warning */}
          {user && user.creditsBalance < 10 && (
            <div className="mt-4 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <div className="flex items-center gap-2 text-yellow-800">
                <AlertCircle className="h-4 w-4" />
                <span className="text-sm font-medium">Low Credits</span>
              </div>
              <p className="text-xs text-yellow-700 mt-1">
                You have {user.creditsBalance} credits remaining. Each page costs 1 credit to process.
              </p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}